import { ActionCreator, ActionCreatorsMapObject, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import axios from 'axios';
import {
    IWorkspaceAction,
    IWorkspaceState,
} from '../../common/model/WorkspaceModels';
import { showNotification } from '../Notifications/NotificationActions';
import { sec } from '../../security';
import config from '../../auth.config';
import {
    AREA_CONFIGURATION_REQUEST,
    AREA_CONFIGURATION_SUCCESS,
} from './Dashboard/DashboardReducer';
import { types as AuditTypes } from '../Audit/AuditActions';
import fileDownload from 'js-file-download';
import * as caseActions from './Case/CaseActions';

const API_ROOT = process.env.REACT_APP_API_ROOT;

export const types = {
    GET_WORKSPACE_LIST_REQUEST: 'GET_WORKSPACE_LIST_REQUEST',
    GET_WORKSPACE_LIST_SUCCESS: 'GET_WORKSPACE_LIST_SUCCESS',
    GET_WORKSPACE_LIST_FAILURE: 'GET_WORKSPACE_LIST_FAILURE',
    CREATE_WORKSPACE_REQUEST: 'CREATE_WORKSPACE_REQUEST',
    CREATE_WORKSPACE_SUCCESS: 'CREATE_WORKSPACE_SUCCESS',
    CREATE_WORKSPACE_FAILURE: 'CREATE_WORKSPACE_FAILURE',
    GET_WORKSPACE_REQUEST: 'GET_WORKSPACE_REQUEST',
    GET_WORKSPACE_SUCCESS: 'GET_WORKSPACE_SUCCESS',
    GET_WORKSPACE_FAILURE: 'GET_WORKSPACE_FAILURE',
    WORKSPACE_QUERY_REQUEST: 'WORKSPACE_QUERY_REQUEST',
    WORKSPACE_QUERY_SUCCESS: 'WORKSPACE_QUERY_SUCCESS',
    WORKSPACE_QUERY_FAILURE: 'WORKSPACE_QUERY_FAILURE',
    CREATE_DUMMY_REQUEST: 'CREATE_DUMMY_REQUEST',
    CREATE_DUMMY_SUCCESS: 'CREATE_DUMMY_SUCCESS',
    CREATE_DUMMY_FAILURE: 'CREATE_DUMMY_FAILURE',
    SET_WORKSPACE_CONFIG_REQUEST: 'SET_WORKSPACE_CONFIG_REQUEST',
    SET_WORKSPACE_CONFIG_SUCCESS: 'SET_WORKSPACE_CONFIG_SUCCESS',
    SET_WORKSPACE_CONFIG_FAILURE: 'SET_WORKSPACE_CONFIG_FAILURE',
    RESET_WORKSPACE: 'RESET_WORKSPACE',
    UPDATE_WORKSPACE_REQUEST: 'UPDATE_WORKSPACE_REQUEST',
    UPDATE_WORKSPACE_SUCCESS: 'UPDATE_WORKSPACE_SUCCESS',
    UPDATE_WORKSPACE_FAILURE: 'UPDATE_WORKSPACE_FAILURE',
    DOWNLOAD_TEMPLATE_REQUEST: 'DOWNLOAD_TEMPLATE_REQUEST',
    DOWNLOAD_TEMPLATE_SUCCESS: 'DOWNLOAD_TEMPLATE_SUCCESS',
    DOWNLOAD_TEMPLATE_FAILURE: 'DOWNLOAD_TEMPLATE_FAILURE',
    INITIATE_FILE_IMPORT_REQUEST: 'INITIATE_FILE_IMPORT_REQUEST',
    INITIATE_FILE_IMPORT_SUCCESS: 'INITIATE_FILE_IMPORT_SUCCESS',
    FILE_IMPORT_PROGRESS_REQUEST: 'FILE_IMPORT_PROGRESS_REQUEST',
    FILE_IMPORT_PROGRESS_COMPLETE: 'FILE_IMPORT_PROGRESS_COMPLETE',
    FILE_IMPORT_PROGRESS: 'FILE_IMPORT_PROGRESS',
    INITIATE_FILE_IMPORT_FAILURE: 'INITIATE_FILE_IMPORT_FAILURE',
    SAVE_FILE_IMPORT_MAPPING_REQUEST: 'SAVE_FILE_IMPORT_MAPPING_REQUEST',
    SAVE_FILE_IMPORT_MAPPING_SUCCESS: 'SAVE_FILE_IMPORT_MAPPING_SUCCESS',
    SAVE_FILE_IMPORT_MAPPING_FAILURE: 'SAVE_FILE_IMPORT_MAPPING_FAILURE',
    FILE_UPLOAD_REQUEST: 'FILE_UPLOAD_REQUEST',
    FILE_UPLOAD_SUCCESS: 'FILE_UPLOAD_SUCCESS',
    FILE_UPLOAD_FAILURE: 'FILE_UPLOAD_FAILURE',
    GET_IMPORT_STATUS_REQUEST: 'GET_IMPORT_STATUS_REQUEST',
    GET_IMPORT_STATUS_SUCCESS: 'GET_IMPORT_STATUS_SUCCESS',
    GET_IMPORT_STATUS_FAILURE: 'GET_IMPORT_STATUS_FAILURE',
    GET_FILE_LIST_REQUEST: 'GET_FILE_LIST_REQUEST',
    GET_FILE_LIST_SUCCESS: 'GET_FILE_LIST_SUCCESS',
    GET_FILE_LIST_FAILURE: 'GET_FILE_LIST_FAILURE',
    DELETE_DRAFT_IMPORT_REQUEST: 'DELETE_DRAFT_IMPORT_REQUEST',
    DELETE_DRAFT_IMPORT_SUCCESS: 'DELETE_DRAFT_IMPORT_SUCCESS',
    DELETE_DRAFT_IMPORT_FAILURE: 'DELETE_DRAFT_IMPORT_FAILURE',
    RESET_FILE_IMPORT_STATUS: 'RESET_FILE_IMPORT_STATUS',
    RESET_FILE_LIST: 'RESET_FILE_LIST',
    GET_WORKSPACE_USER_ROLE_REQUEST: 'GET_WORKSPACE_USER_ROLE_REQUEST',
    GET_WORKSPACE_USER_ROLE_SUCCESS: 'GET_WORKSPACE_USER_ROLE_SUCCESS',
    GET_WORKSPACE_USER_ROLE_FAILURE: 'GET_WORKSPACE_USER_ROLE_FAILURE',
};

export const getWorkspaceList: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> =
    (limit: number, skip: number, sort: string, direction: string) =>
    async (dispatch: Dispatch) => {
        const access_token = await sec.getAccessTokenSilently()({
            audience: config.audience,
        });

        dispatch({
            type: types.GET_WORKSPACE_LIST_REQUEST,
        });

        try {
            const response = await axios({
                method: 'GET',
                url: `${API_ROOT}/workspace/list`,
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json;charset=UTF-8',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + access_token,
                },
                params: {
                    skip,
                    limit,
                    sort,
                    direction,
                },
            });

            dispatch({
                type: types.GET_WORKSPACE_LIST_SUCCESS,
                payload: {
                    data: response.data,
                    error: undefined,
                },
            });
        } catch (error) {
            console.log('Get Workspace List Error', error);
            dispatch({
                type: types.GET_WORKSPACE_LIST_FAILURE,
                error: 'There was an issue fetching the workspace data. Please try again later.',
            });
        }
    };

export const createWorkspace: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (data) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.CREATE_WORKSPACE_REQUEST,
    });

    try {
        const response = await axios({
            method: 'POST',
            url: `${API_ROOT}/workspace/`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
            data,
        });

        dispatch({
            type: types.CREATE_WORKSPACE_SUCCESS,
            payload: {
                data: response.data,
                error: undefined,
            },
        });
        return response.data;
    } catch (error) {
        console.log('Create Workspace Error', error);
        dispatch({
            type: types.CREATE_WORKSPACE_FAILURE,
            error: 'There was an issue creating the workspace. Please try again later.',
        });
    }
};

export const getWorkspace: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (id: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.GET_WORKSPACE_REQUEST,
    });

    try {
        const response = await axios({
            method: 'GET',
            url: `${API_ROOT}/workspace/${id}`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.GET_WORKSPACE_SUCCESS,
            payload: {
                data: response.data,
                error: undefined,
            },
        });
    } catch (error) {
        console.log('Get Workspace List Error', error);
        dispatch({
            type: types.GET_WORKSPACE_FAILURE,
            error: 'There was an issue fetching the workspace data. Please try again later.',
        });
    }
};

export const updateWorkspace: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (id: string, accountInfo: any) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.UPDATE_WORKSPACE_REQUEST,
    });

    try {
        const response = await axios({
            method: 'PUT',
            url: `${API_ROOT}/workspace/${id}`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
            data: accountInfo,
        });

        const payload = {
            name: response.data.name,
        };

        const newActivity = {
            action: 'UPDATE',
            collectionName: 'workspaces',
            createdAt: response.data.createdAt,
            createdBy: 'system',
            lastUpdated: response.data.lastUpdated,
            userId: response.data.updatedBy,
            transactionId: null,
            data: JSON.stringify({ payload: JSON.stringify(payload) }),
        };

        dispatch({
            type: types.UPDATE_WORKSPACE_SUCCESS,
            payload: {
                data: response.data,
                error: undefined,
            },
        });
        dispatch({
            type: AuditTypes.NEW_ACTIVITIES_SUCCESS,
            payload: newActivity,
            id,
        });
    } catch (error) {
        console.log('Get Workspace List Error', error);
        dispatch({
            type: types.UPDATE_WORKSPACE_FAILURE,
            error: 'There was an issue fetching the workspace data. Please try again later.',
        });
    }
};

export const setWorkspaceConfiguration: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> =
    (id: string, configuration: string, data: any) =>
    async (dispatch: Dispatch) => {
        const access_token = await sec.getAccessTokenSilently()({
            audience: config.audience,
        });

        dispatch({
            type: types.SET_WORKSPACE_CONFIG_REQUEST,
            payload: {
                configType: configuration,
            },
        });

        try {
            await axios({
                method: 'PUT',
                url: `${API_ROOT}/workspace/${id}/configuration/${configuration}`,
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json;charset=UTF-8',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + access_token,
                },
                data,
            });

            if (configuration === 'dashboard-configuration') {
                dispatch({
                    type: AREA_CONFIGURATION_SUCCESS,
                    payload: data,
                    configType: configuration,
                    id,
                });
            } else {
                dispatch({
                    type: types.SET_WORKSPACE_CONFIG_SUCCESS,
                    payload: {
                        configType: configuration,
                        data,
                        error: undefined,
                    },
                });
            }
            if (configuration === 'saved-filters-configuration') {
                await dispatch(
                    showNotification(
                        'Successfully updated saved filters templates.',
                        'success'
                    )
                );
            }
        } catch (error) {
            console.log('Create Workspace Configuration Error', error);
            dispatch({
                type: types.SET_WORKSPACE_CONFIG_FAILURE,
                error: 'There was an issue creating the workspace configuration. Please try again later.',
            });
            if (configuration === 'saved-filters-configuration') {
                await dispatch(
                    showNotification(
                        'There was an issue saving your filters template. Please try again later.',
                        'error'
                    )
                );
            }
        }
    };

export const getWorkspaceConfiguration: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (id: string, configuration: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    let url = `${API_ROOT}/workspace/${id}/configuration`;
    if (configuration) {
        url = `${API_ROOT}/workspace/${id}/configuration/${configuration}`;
    }

    if (configuration === 'dashboard-configuration') {
        dispatch({ type: AREA_CONFIGURATION_REQUEST, id });
    } else {
        dispatch({
            type: types.SET_WORKSPACE_CONFIG_REQUEST,
            payload: {
                configType: configuration,
            },
        });
    }
    try {
        const response = await axios({
            method: 'GET',
            url,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        if (configuration === 'dashboard-configuration') {
            let configPayload = {
                ...response.data,
            };
            dispatch({
                type: AREA_CONFIGURATION_SUCCESS,
                payload: configPayload,
                configType: configuration,
                id,
            });

            return configPayload;
        } else {
            dispatch({
                type: types.SET_WORKSPACE_CONFIG_SUCCESS,
                payload: {
                    configType: configuration,
                    data: response.data,
                    error: undefined,
                },
            });
        }
    } catch (error) {
        console.log('Get Workspace Configuration Error', error);
        dispatch({
            type: types.SET_WORKSPACE_CONFIG_FAILURE,
            error: 'There was an issue creating the workspace configuration. Please try again later.',
        });
    }
};

export const getWorkspaceQueryData: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (queryType: string, workspaceId: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.WORKSPACE_QUERY_REQUEST,
    });

    try {
        const response = await axios({
            method: 'POST',
            url: `${API_ROOT}/${queryType}/dashboard/${workspaceId}/query`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
            data: {
                addCount: true,
            },
        });

        dispatch({
            type: types.WORKSPACE_QUERY_SUCCESS,
            payload: {
                data: response.data,
                type: queryType,
                error: undefined,
            },
        });
    } catch (error) {
        console.log('Fetch Workspace Query Error', error);
        dispatch({
            type: types.WORKSPACE_QUERY_FAILURE,
            error: 'There was an issue fetching the workspace query data. Please try again later.',
        });
    }
};

export const createDummyCases: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (workspaceId: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.CREATE_DUMMY_REQUEST,
    });

    try {
        const response = await axios({
            method: 'POST',
            url: `${API_ROOT}/workspace/${workspaceId}/dummy-data`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });
        return response;
    } catch (error) {
        console.log('Fetch Workspace Query Error', error);
        dispatch({
            type: types.CREATE_DUMMY_FAILURE,
            error: 'There was an issue creating dummy data for the workspace. Please try again later.',
        });
        await dispatch(
            showNotification(
                'There was an issue creating demo data for the workspace. Please try again later.',
                'error'
            )
        );
    }
};

export const downloadTemplates: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (template: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.DOWNLOAD_TEMPLATE_REQUEST,
    });

    try {
        const response = await axios({
            method: 'GET',
            url: `${API_ROOT}/templates/${template}`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
            responseType: 'blob',
        });

        fileDownload(response.data, `HDE_${template}_Template.xlsx`);
        await dispatch(
            showNotification('Template successfully downloaded.', 'success')
        );
    } catch (error) {
        console.log('Get Template Error', error);
        dispatch({
            type: types.DOWNLOAD_TEMPLATE_FAILURE,
            error: 'There was an issue downloading the template. Please try again later.',
        });
    }
};

export const completeImport: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = () => async (dispatch: Dispatch) => {
    dispatch({
        type: types.CREATE_DUMMY_SUCCESS,
    });
    dispatch(
        showNotification('Data has been successfully imported.', 'success')
    );
};

export const initiateFileImport: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (workspaceId: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.INITIATE_FILE_IMPORT_REQUEST,
    });

    try {
        const response = await axios({
            method: 'POST',
            url: `${API_ROOT}/workspace/${workspaceId}/upload/init`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.INITIATE_FILE_IMPORT_SUCCESS,
            payload: {
                data: response.data,
                type: 'initiate',
                error: undefined,
            },
        });
        return response.data;
    } catch (error) {
        console.log('Upload Initiation Error', error);
        dispatch({
            type: types.INITIATE_FILE_IMPORT_FAILURE,
            error: 'There was an issue initiating your file import. Please try again later.',
        });
    }
};

export const saveFileImportMapping: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (fileImportId: string, data: any) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.SAVE_FILE_IMPORT_MAPPING_REQUEST,
    });

    try {
        const response = await axios({
            method: 'PUT',
            url: `${API_ROOT}/file/import/${fileImportId}/mapping`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
            data,
        });

        dispatch({
            type: types.SAVE_FILE_IMPORT_MAPPING_SUCCESS,
            payload: {
                data: response.data,
                error: undefined,
            },
        });
    } catch (error) {
        console.log('File Mapping Error', error);
        dispatch({
            type: types.SAVE_FILE_IMPORT_MAPPING_FAILURE,
            error: 'There was an issue saving your file import mapping. Please try again later.',
        });
    }
};

export const uploadFile: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> =
    (fileImportId: string, file: any, fileType: string) =>
    async (dispatch: Dispatch) => {
        const access_token = await sec.getAccessTokenSilently()({
            audience: config.audience,
        });
        const formData = new FormData();
        formData.append('file', file.data, file.name);

        dispatch({
            type: types.FILE_UPLOAD_REQUEST,
        });

        let headers: {
            Authorization: string;
            Accept: string;
            'Access-Control-Allow-Origin': string;
            'Content-Type': string;
            FileType?: string;
        } = {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json',
            Authorization: 'Bearer ' + access_token,
            FileType: fileType,
        };

        if (fileType === 'ZIP') {
            headers = {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'multipart/form-data',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            };
        }

        try {
            const response = await axios({
                method: 'PUT',
                url: `${API_ROOT}/file/import/${fileImportId}/upload`,
                headers,
                data: formData,
            });

            dispatch({
                type: types.INITIATE_FILE_IMPORT_SUCCESS,
                payload: {
                    data: response.data,
                    type: 'upload',
                    error: undefined,
                },
            });
        } catch (error) {
            console.log('File Upload Error', error);
            dispatch({
                type: types.FILE_UPLOAD_FAILURE,
                error: 'There was an issue uploading your file. Please try again later.',
            });
        }
    };

export const getImportStatus: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (fileImportId: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.GET_IMPORT_STATUS_REQUEST,
    });

    try {
        const response = await axios({
            method: 'GET',
            url: `${API_ROOT}/file/import/${fileImportId}`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.INITIATE_FILE_IMPORT_SUCCESS,
            payload: {
                data: response.data,
                type: 'status',
                error: undefined,
            },
        });
    } catch (error) {
        console.log('Get Workspace List Error', error);
        dispatch({
            type: types.GET_IMPORT_STATUS_FAILURE,
            error: 'There was an issue fetching the workspace data. Please try again later.',
        });
    }
};

export const getFileList: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> =
    (
        workspaceId: string,
        limit: number,
        skip: number,
        sort: string,
        direction: string,
        type: string
    ) =>
    async (dispatch: Dispatch) => {
        const access_token = await sec.getAccessTokenSilently()({
            audience: config.audience,
        });

        dispatch({
            type: types.GET_FILE_LIST_REQUEST,
            payload: {
                type,
            },
        });

        try {
            const response = await axios({
                method: 'GET',
                url: `${API_ROOT}/file/list`,
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json;charset=UTF-8',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + access_token,
                },
                params: {
                    workspaceId,
                    skip,
                    limit,
                    sort,
                    direction,
                },
            });

            dispatch({
                type: types.GET_FILE_LIST_SUCCESS,
                payload: {
                    data: response.data,
                    type: type,
                    error: undefined,
                },
            });
        } catch (error) {
            console.log('Get File Import List Error', error);
            dispatch({
                type: types.GET_FILE_LIST_FAILURE,
                error: 'There was an issue fetching the file import data. Please try again later.',
            });
        }
    };

export const getFileImportProgress: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (fileIds: string[]) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.FILE_IMPORT_PROGRESS_REQUEST,
    });
    return axios({
        url: `${API_ROOT}/file/updates`,
        data: fileIds,
        headers: {
            accept: '*',
            'content-type': 'application/json',
            Authorization: `Bearer ${access_token}`,
        },
        method: 'POST',
        onDownloadProgress: (progressEvent) => {
            const xhr = progressEvent.event.target;
            const { responseText } = xhr;
            // console.log('=====responseText======');
            // console.log(responseText);
            // remove empty strings, combine values in 3 (not JSON parsable so doing hacky things)
            const resolvedData = responseText.split('\n');
            const files = [];
            const output = resolvedData.filter(Boolean).reduce((a, str, i) => {
                if (i % 3 === 0) {
                    a.push(str + ',');
                } else {
                    a[a.length - 1] += str + ',';
                }
                return a;
            }, []);
            // get important information ie file ID and data
            // eslint-disable-next-line array-callback-return
            output.map((item: string, i: any) => {
                const parsedData = item
                    .split('data:')
                    .pop()
                    .split(',id:')
                    .shift();
                files.push(JSON.parse(parsedData));
            });
            // eslint-disable-next-line array-callback-return
            files.map((item) => {
                dispatch({
                    type: types.FILE_IMPORT_PROGRESS,
                    payload: {
                        data: item,
                        type: 'status',
                        error: undefined,
                    },
                });
            });
        },
    })
        .then(({ data }) => {
            Promise.resolve(data);
            console.log('=====File Import SSE Resolved=====');
            dispatch({
                type: types.FILE_IMPORT_PROGRESS_COMPLETE,
            });
            const delay = setTimeout(() => {
                dispatch(caseActions?.checkFileTrustness(fileIds));
            }, 5000);
            return () => clearTimeout(delay);
        })
        .catch((error) => {
            // dispatch(
            //     showNotification(
            //         (error.response &&
            //             error.response.data &&
            //             error.response.data.message) ||
            //             'Error checking file trustness',
            //         'error'
            //     )
            // );
            console.log('FILE IMPORT ERROR', error);
            dispatch({ type: types.FILE_IMPORT_PROGRESS_COMPLETE });
            // dispatch(
            //     failedFileTrustness(
            //         (error.response && error.response.data) || error
            //     )
            // );
        });
};

export const deleteDraftImport: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (fileImportId: string) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.DELETE_DRAFT_IMPORT_REQUEST,
    });

    try {
        const response = await axios({
            method: 'DELETE',
            url: `${API_ROOT}/file/import/${fileImportId}`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.DELETE_DRAFT_IMPORT_SUCCESS,
            payload: {
                data: response.data,
                fileImportId,
                error: undefined,
            },
        });
    } catch (error) {
        console.log('Delete File Import Draft Error', error);
        dispatch({
            type: types.DELETE_DRAFT_IMPORT_FAILURE,
            error: 'There was an issue deleting the draft file import. Please try again later.',
        });
    }
};

export const getWorkspaceUserRole: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = (workspaceId: string, userId: number) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        audience: config.audience,
    });

    dispatch({
        type: types.GET_WORKSPACE_USER_ROLE_REQUEST,
    });

    try {
        const response = await axios({
            method: 'GET',
            url: `${API_ROOT}/workspace/${workspaceId}/access?userId=${userId}`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.GET_WORKSPACE_USER_ROLE_SUCCESS,
            payload: {
                data: response.data,
                error: undefined,
            },
        });
    } catch (error) {
        console.log('Get User Role Error', error);
        dispatch({
            type: types.GET_WORKSPACE_USER_ROLE_FAILURE,
            error: 'There was an issue fetching the user role for this workspace. Please try again later.',
        });
    }
};

export const resetWorkspace: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = () => async (dispatch: Dispatch) =>
    dispatch({
        type: types.RESET_WORKSPACE,
    });

export const resetFileImportStatus: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = () => async (dispatch: Dispatch) =>
    dispatch({
        type: types.RESET_FILE_IMPORT_STATUS,
    });

export const resetFileList: ActionCreator<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = () => async (dispatch: Dispatch) =>
    dispatch({
        type: types.RESET_FILE_LIST,
    });

export const workspaceActions: ActionCreatorsMapObject<
    ThunkAction<Promise<any>, IWorkspaceState, null, IWorkspaceAction>
> = {
    getWorkspaceList,
    createWorkspace,
    getWorkspace,
    resetWorkspace,
    setWorkspaceConfiguration,
    getWorkspaceQueryData,
    createDummyCases,
    completeImport,
    updateWorkspace,
    downloadTemplates,
    initiateFileImport,
    saveFileImportMapping,
    uploadFile,
    getImportStatus,
    getFileList,
    resetFileImportStatus,
    resetFileList,
    deleteDraftImport,
    getWorkspaceConfiguration,
    getWorkspaceUserRole,
    getFileImportProgress,
};
